import React, { useEffect, useMemo, useState } from "react";
import { cx, hardNavigateTo, useTranslations } from "@jugl-web/utils";
import { isValidEmail } from "@jugl-web/utils/validators";
import { apiErrorHasCode, useRestApiProvider } from "@jugl-web/rest-api";
import { MAX_WORKSPACE_NAME_LENGTH } from "@web-src/modules/auth/conts";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import { EntityLimitReachedAlert } from "@web-src/modules/common/components/EntityLimitReachedAlert";
import {
  Button,
  Dialog,
  PageLoaderFull,
  PlainButton,
  TextInput,
  CountryPicker,
  CountryPickerValue,
} from "@jugl-web/ui-kit";
import { useDefaultCountry } from "../../hooks/useDefaultCountry";
import { ReactComponent as CrossIcon } from "../CXSignupDialog/assets/cross.svg";
import instagramShadowImage from "../CXSignupDialog/assets/instagram-shadow.png";
import instagramGhostImage from "../CXSignupDialog/assets/instagram-ghost.png";
import whatsappShadowImage from "../CXSignupDialog/assets/whatsapp-shadow.png";
import whatsappGhostImage from "../CXSignupDialog/assets/whatsapp-ghost.png";
import webShadowImage from "../CXSignupDialog/assets/web-shadow.png";
import webGhostImage from "../CXSignupDialog/assets/web-ghost.png";
import twilioShadowImage from "../CXSignupDialog/assets/twilio-shadow.png";
import twilioGhostImage from "../CXSignupDialog/assets/twilio-ghost.png";
import { ReactComponent as ArrowIcon } from "./assets/arrow.svg";

const slides = [
  { ghost: instagramGhostImage, shadow: instagramShadowImage },
  { ghost: whatsappGhostImage, shadow: whatsappShadowImage },
  { ghost: webGhostImage, shadow: webShadowImage },
  { ghost: twilioGhostImage, shadow: twilioShadowImage },
];

export const CreateCXWorkspaceDialog: React.FC<{
  isOpen: boolean;
  onRequestClose: () => void;
}> = ({ isOpen, onRequestClose }) => {
  const { t } = useTranslations();
  const { entitiesApi } = useRestApiProvider();
  const { generateUrl } = useNavigation();
  const [createEntity, { isLoading }] = entitiesApi.useCreateEntityMutation();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [workspaceName, setWorkspaceName] = useState("");
  const [workspaceCountry, setWorkspaceCountry] =
    useState<CountryPickerValue>();
  const [workspaceEmail, setWorkspaceEmail] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLimitAlertOpen, setIsLimitAlertOpen] = useState(false);
  const [isNavigatingAway, setIsNavigatingAway] = useState(false);

  const defaultCountry = useDefaultCountry();

  useEffect(() => {
    if (defaultCountry && !workspaceCountry) {
      setWorkspaceCountry(defaultCountry);
    }
  }, [defaultCountry, workspaceCountry]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const interval = setInterval(() => {
      setCurrentIndex((c) => (c + 1) % slides.length);
    }, 7000);
    return () => clearInterval(interval);
  }, [isOpen]);

  const handleClose = () => {
    onRequestClose();
    setWorkspaceName("");
    setWorkspaceCountry(undefined);
    setWorkspaceEmail("");
    setIsSubmitted(false);
  };

  const workspaceEmailError = useMemo(() => {
    if (!isSubmitted) return undefined;
    if (!workspaceEmail.length) {
      return t({
        id: "form-controls.email.error-is-required",
        defaultMessage: "Email is empty",
      });
    }
    if (!isValidEmail(workspaceEmail)) {
      return t({
        id: "form-controls.email.error-is-incorrect",
        defaultMessage: "Email is incorrect",
      });
    }
    return undefined;
  }, [isSubmitted, workspaceEmail, t]);

  const allFieldsHaveValues =
    !!workspaceName.trim() && !!workspaceCountry?.iso && !!workspaceEmail;

  const handleSubmit = async () => {
    setIsSubmitted(true);
    if (
      !workspaceName.trim() ||
      !workspaceCountry?.iso ||
      !isValidEmail(workspaceEmail)
    ) {
      return;
    }

    try {
      const result = await createEntity({
        type: "cx",
        data: {
          name: workspaceName.trim(),
          country: workspaceCountry.iso,
          billing_email: workspaceEmail ?? undefined,
        },
        meta: {
          onError: (error) => {
            if (apiErrorHasCode(error, "max_limit_reached")) {
              setIsLimitAlertOpen(true);
              return undefined;
            }
            return "unhandled";
          },
        },
      }).unwrap();

      // The dialog stays mounted behind a blocking loader: closing it here would let
      // the app show through while the browser is still fetching the new document.
      setIsNavigatingAway(true);
      hardNavigateTo(
        generateUrl("cxAIAgents", undefined, { entityId: result.id })
      );
    } catch {
      // Error toast is handled globally by the API result pipeline
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      className="fixed flex h-full max-h-[min(783px,95vh)] w-full max-w-[min(1310px,95%)] min-w-0 items-center overflow-hidden rounded-2xl bg-white"
    >
      <PlainButton
        className="absolute top-5 right-5 z-10 flex h-8 w-8 items-center justify-center"
        onClick={handleClose}
      >
        <CrossIcon />
      </PlainButton>
      <div className="relative mr-12 hidden h-full w-[476px] shrink-0 bg-dark [@media(min-width:1170px)_and_(min-height:650px)]:block">
        {slides.map((slide, index) => (
          <div
            key={+index}
            className={cx(
              "pointer-events-none transition-opacity duration-700",
              index === currentIndex ? "opacity-100" : "opacity-0"
            )}
          >
            <img
              src={slide.shadow}
              className="absolute top-1/2 left-0 w-3/4 -translate-y-1/2"
              alt=""
            />
            <img
              src={slide.ghost}
              className="absolute top-1/2 left-0 w-[510px] -translate-y-1/2"
              alt=""
            />
          </div>
        ))}
      </div>
      <div className="jugl__custom-scrollbar flex h-full w-full min-w-0 flex-col items-center justify-center gap-4 overflow-y-auto px-4 py-4 sm:gap-8 sm:px-10">
        <div className="my-auto flex w-full max-w-[500px] flex-col items-center justify-center py-2 sm:py-4">
          <span className="text-center font-secondary text-[28px] leading-9 font-medium text-dark sm:text-[32px] sm:leading-[48px]">
            {t({
              id: "cx-ai-agents-page.lets-start-with-essentials",
              defaultMessage: "Let's start with essentials",
            })}
          </span>
          <span className="mt-2 text-center font-secondary text-sm leading-4 text-grey-900">
            {t({
              id: "cx-ai-agents-page.just-a-few-details",
              defaultMessage: "Just a few details to start with",
            })}
          </span>
          <div className="mt-6 flex w-full flex-col gap-6 sm:mt-10">
            <div className="flex w-full flex-col gap-4 sm:flex-row">
              <div className="w-full min-w-0 sm:flex-[6_6_0%]">
                <TextInput
                  placeholder={t({
                    id: "common.enter",
                    defaultMessage: "Enter",
                  })}
                  label={t({
                    id: "create-entity-form.workspace-name",
                    defaultMessage: "Workspace Name",
                  })}
                  isRequired
                  value={workspaceName}
                  onChange={(e) => setWorkspaceName(e.target.value)}
                  isInvalid={isSubmitted && !workspaceName.trim()}
                  classNames={{ wrapperClassName: "w-full" }}
                  maxLength={MAX_WORKSPACE_NAME_LENGTH}
                />
              </div>
              <div className="w-full min-w-0 sm:flex-[4_4_0%]">
                <CountryPicker
                  countryIso={workspaceCountry?.iso}
                  onSelect={(item) => setWorkspaceCountry(item)}
                  placement="top"
                  className="z-[9999]"
                >
                  {() => (
                    <TextInput
                      value={
                        workspaceCountry?.name
                          ? `${workspaceCountry.name} (${workspaceCountry.iso})`
                          : ""
                      }
                      isRequired
                      label={t({
                        id: "create-entity-form.workspace-country",
                        defaultMessage: "Workspace Country",
                      })}
                      placeholder={t({
                        id: "common.select",
                        defaultMessage: "Select",
                      })}
                      classNames={{
                        wrapperClassName: "w-full",
                        inputClassName: "cursor-pointer",
                      }}
                      isInvalid={isSubmitted && !workspaceCountry?.iso}
                      readOnly
                    />
                  )}
                </CountryPicker>
              </div>
            </div>
            <TextInput
              placeholder="email@gmail.com"
              label={t({
                id: "create-entity-form.workspace-email",
                defaultMessage: "Workspace Email",
              })}
              isRequired
              value={workspaceEmail}
              onChange={(e) => setWorkspaceEmail(e.target.value)}
              isInvalid={isSubmitted && !!workspaceEmailError}
              errorMessage={workspaceEmailError}
              type="email"
            />
          </div>
          <Button
            className="mt-8 w-full sm:mt-10"
            onClick={handleSubmit}
            isDisabled={!allFieldsHaveValues || isLoading || isNavigatingAway}
            iconEnd={<ArrowIcon />}
            isCx
          >
            {t({
              id: "cx-ai-agents-page.create-workspace",
              defaultMessage: "Create Workspace",
            })}
          </Button>
        </div>
      </div>
      <EntityLimitReachedAlert
        isOpen={isLimitAlertOpen}
        onClose={() => setIsLimitAlertOpen(false)}
      />
      <PageLoaderFull isActive={isNavigatingAway} />
    </Dialog>
  );
};
